import { useMemo, useRef, useState } from 'react';
import { usePlanification } from '../../context/PlanificationContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import domtoimage from 'dom-to-image-more';
import jsPDF from 'jspdf';
import './BilanEquipe.css';

// Couleurs pour les statuts de tâches
const STATUS_COLORS = {
  completed: '#10b981',    // Vert
  enCours: '#3b82f6',      // Bleu
  ouvert: '#f59e0b',       // Orange
  enAttente: '#8b5cf6',    // Violet
  aValider: '#06b6d4',     // Cyan
  bloque: '#ef4444',       // Rouge
  annule: '#6b7280'        // Gris
};

const BilanEquipe = () => {
  const { planificationData, lastParams } = usePlanification();
  const bilanRef = useRef(null);
  const performanceRef = useRef(null);
  const repartitionRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPerformance, setIsExportingPerformance] = useState(false);
  const [isExportingRepartition, setIsExportingRepartition] = useState(false);
  const [focusedPerson, setFocusedPerson] = useState(null);
  const [isExportingPersonModal, setIsExportingPersonModal] = useState(false);
  const personModalRef = useRef(null);
  const [taskDetailsModal, setTaskDetailsModal] = useState(null); // { projectName, type: 'P' ou 'R', tasks: [] }

  const openPersonReport = (person) => {
    if (!person) return;
    setFocusedPerson(person);
  };

  // Fonction pour extraire les projets avec leurs totaux P et R par personne
  const getPersonProjectsWithTotals = useMemo(() => {
    return (personKey) => {
      if (!planificationData?.hierarchie) return [];
      
      const projectsMap = new Map(); // Map<projectName, {totalP, totalR, status}>
      const hierarchie = planificationData.hierarchie;
      
      // Parcourir tous les groupes
      Object.values(hierarchie).forEach((groupe) => {
        // Parcourir tous les projets
        Object.entries(groupe).forEach(([projectName, projectGroup]) => {
          // projectGroup est maintenant un objet avec { status, categories }
          const projectStatus = projectGroup.status || null;
          const categories = projectGroup.categories || projectGroup; // Compatibilité avec ancienne structure
          
          // Parcourir toutes les catégories
          Object.values(categories).forEach((categorie) => {
            // Vérifier si cette personne a des tâches dans cette catégorie
            const personData = categorie[personKey];
            if (personData && personData.taches) {
              // Parcourir les tâches de cette personne
              personData.taches.forEach((tache) => {
                const nombreJour = parseFloat(tache.nombreJour || 0);
                const billableHours = parseFloat(tache.billableHours || 0);
                const joursRealises = billableHours / 7; // Convertir heures en jours
                
                // Normaliser le nom du projet pour éviter les doublons dus aux espaces/casse
                const normalizedProjectName = projectName.trim();
                
                // Initialiser ou mettre à jour les totaux pour ce projet
                if (!projectsMap.has(normalizedProjectName)) {
                  projectsMap.set(normalizedProjectName, { totalP: 0, totalR: 0, status: projectStatus });
                }
                
                const projectData = projectsMap.get(normalizedProjectName);
                projectData.totalP += nombreJour;
                projectData.totalR += joursRealises;
              });
            }
          });
        });
      });
      
      // Convertir la Map en tableau d'objets et trier alphabétiquement
      // La Map garantit déjà l'unicité des noms de projets
      return Array.from(projectsMap.entries())
        .map(([projectName, data]) => ({
          projectName,
          totalP: data.totalP,
          totalR: data.totalR,
          status: data.status
        }))
        .sort((a, b) => a.projectName.localeCompare(b.projectName));
    };
  }, [planificationData]);

  // Fonction pour récupérer les tâches d'un projet spécifique pour une personne
  const getProjectTasks = (personKey, projectName) => {
    if (!planificationData?.hierarchie) return [];
    
    const tasks = [];
    const hierarchie = planificationData.hierarchie;
    
    // Parcourir tous les groupes
    Object.values(hierarchie).forEach((groupe) => {
      // Parcourir tous les projets
      Object.entries(groupe).forEach(([pName, projectGroup]) => {
        const normalizedPName = pName.trim();
        
        // Vérifier si c'est le projet recherché
        if (normalizedPName === projectName) {
          const categories = projectGroup.categories || projectGroup;
          
          // Parcourir toutes les catégories
          Object.values(categories).forEach((categorie) => {
            const personData = categorie[personKey];
            if (personData && personData.taches) {
              personData.taches.forEach((tache) => {
                tasks.push({
                  nomTache: tache.nomTache,
                  nombreJour: parseFloat(tache.nombreJour || 0),
                  billableHours: parseFloat(tache.billableHours || 0),
                  joursRealises: parseFloat(tache.billableHours || 0) / 7,
                  status: tache.status,
                  categorieTache: tache.categorieTache
                });
              });
            }
          });
        }
      });
    });
    
    return tasks;
  };

  // Fonction d'export PDF pour le modal de la personne avec mise en page intelligente
  const exportPersonModalToPDF = async () => {
    if (!focusedPerson) return;
    
    setIsExportingPersonModal(true);
    
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      
      // Récupérer les projets pour le graphique
      const projects = getPersonProjectsWithTotals(focusedPerson.key).filter(p => p.totalP > 0 || p.totalR > 0);
      
      // Fonction pour ajouter une nouvelle page si nécessaire
      const checkPageBreak = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };
      
      // 1. HEADER - Nom de la personne
      pdf.setFillColor(59, 130, 246); // Bleu
      pdf.rect(margin, yPosition, contentWidth, 20, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(focusedPerson.name, pageWidth / 2, yPosition + 13, { align: 'center' });
      yPosition += 25;
      
      // 2. PÉRIODE
      pdf.setFillColor(219, 234, 254); // Bleu clair
      pdf.rect(margin, yPosition, contentWidth, 10, 'F');
      pdf.setTextColor(30, 64, 175);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const periodText = `Periode : ${lastParams?.dateDebut} -> ${lastParams?.dateFin}`;
      pdf.text(periodText, pageWidth / 2, yPosition + 7, { align: 'center' });
      yPosition += 15;
      
      // 3. STATISTIQUES GÉNÉRALES (2x2 grid)
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Statistiques generales', margin, yPosition);
      yPosition += 8;
      
      const statBoxWidth = (contentWidth - 5) / 2;
      const statBoxHeight = 20;
      const stats = [
        { label: 'Total jours planifies', value: focusedPerson.totalJours, color: [59, 130, 246] },
        { label: 'Total jours realises', value: focusedPerson.sum_jourTravail, color: [16, 185, 129] },
        { label: 'Total taches', value: focusedPerson.nombreTaches, color: [245, 158, 11] },
        { label: 'Taux de realisation', value: `${focusedPerson.pourcentage.toFixed(1)}%`, color: [139, 92, 246] }
      ];
      
      stats.forEach((stat, index) => {
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = margin + col * (statBoxWidth + 5);
        const y = yPosition + row * (statBoxHeight + 5);
        
        // Bordure et fond
        pdf.setDrawColor(226, 232, 240);
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, y, statBoxWidth, statBoxHeight, 2, 2, 'FD');
        
        // Label
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text(stat.label.toUpperCase(), x + statBoxWidth / 2, y + 7, { align: 'center' });
        
        // Value
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...stat.color);
        pdf.text(String(stat.value), x + statBoxWidth / 2, y + 16, { align: 'center' });
      });
      yPosition += (statBoxHeight * 2) + 10 + 10;
      
      checkPageBreak(80);
      
      // 4. GRAPHIQUE PLANIFIÉ VS RÉALISÉ - Nouveau design avec alignement et pagination
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Comparaison Planifie vs Realise', margin, yPosition);
      yPosition += 10;
      
      // Calculer les valeurs
      const totalP = focusedPerson.totalJours;
      const totalR = focusedPerson.sum_jourTravail;
      const maxValue = Math.max(totalP, totalR);
      
      // Dimensions du graphique
      const barWidth = 40;
      const barSpacing = 18;
      const barHeight = 120; // Hauteur totale des barres
      const chartX = margin + 5;
      const nameColumnWidth = 60; // Largeur pour le nom du projet
      
      // Position des deux barres
      const pBarX = chartX + nameColumnWidth;
      const rBarX = pBarX + barWidth + barSpacing;
      
      // Dessiner les titres au-dessus
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text(`Total P: ${totalP.toFixed(1)}j`, pBarX + barWidth / 2, yPosition + 5, { align: 'center' });
      pdf.text(`Total R: ${totalR.toFixed(1)}j`, rBarX + barWidth / 2, yPosition + 5, { align: 'center' });
      
      let barsStartY = yPosition + 10;
      let currentY = barsStartY;
      
      // Dessiner chaque projet aligné avec gestion de pagination
      projects.forEach((project, index) => {
        const maxProjectValue = Math.max(project.totalP, project.totalR);
        const projectHeight = (maxProjectValue / maxValue) * barHeight;
        const minProjectHeight = Math.max(projectHeight, 12); // Hauteur minimale augmentée
        
        // Vérifier si on a besoin d'une nouvelle page
        if (currentY + minProjectHeight + 15 > pageHeight - margin) {
          // Ajouter la légende avant de changer de page
          const legendY = currentY + 5;
          const legendX = chartX + 20;
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          
          pdf.setFillColor(59, 130, 246);
          pdf.rect(legendX, legendY - 1.5, 6, 3.5, 'F');
          pdf.setTextColor(30, 41, 59);
          pdf.text('Planifie (P)', legendX + 8, legendY + 1.5);
          
          pdf.setFillColor(16, 185, 129);
          pdf.rect(legendX + 40, legendY - 1.5, 6, 3.5, 'F');
          pdf.text('Realise (R)', legendX + 48, legendY + 1.5);
          
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(203, 213, 225);
          pdf.rect(legendX + 80, legendY - 1.5, 6, 3.5, 'FD');
          pdf.text('Ecart', legendX + 88, legendY + 1.5);
          
          // Nouvelle page
          pdf.addPage();
          currentY = margin;
          
          // Répéter le titre sur la nouvelle page
          pdf.setTextColor(30, 41, 59);
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comparaison Planifie vs Realise (suite)', margin, currentY);
          currentY += 10;
          
          // Répéter les titres des colonnes
          pdf.setFontSize(11);
          pdf.text(`Total P: ${totalP.toFixed(1)}j`, pBarX + barWidth / 2, currentY + 5, { align: 'center' });
          pdf.text(`Total R: ${totalR.toFixed(1)}j`, rBarX + barWidth / 2, currentY + 5, { align: 'center' });
          currentY += 10;
        }
        
        // Calculer les proportions
        const pProportion = project.totalP > 0 ? (project.totalP / maxProjectValue) : 0;
        const rProportion = project.totalR > 0 ? (project.totalR / maxProjectValue) : 0;
        
        // Barre P (Planifié)
        if (project.totalP > 0) {
          const pHeight = minProjectHeight * pProportion;
          pdf.setFillColor(59, 130, 246);
          pdf.roundedRect(pBarX, currentY, barWidth, pHeight, 1.5, 1.5, 'F');
        }
        // Espace blanc dans P
        if (pProportion < 1) {
          const whiteHeight = minProjectHeight * (1 - pProportion);
          pdf.setDrawColor(203, 213, 225);
          pdf.setLineWidth(0.4);
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(pBarX, currentY + (minProjectHeight * pProportion), barWidth, whiteHeight, 1.5, 1.5, 'FD');
        }
        
        // Barre R (Réalisé)
        if (project.totalR > 0) {
          const rHeight = minProjectHeight * rProportion;
          pdf.setFillColor(16, 185, 129);
          pdf.roundedRect(rBarX, currentY, barWidth, rHeight, 1.5, 1.5, 'F');
        }
        // Espace blanc dans R
        if (rProportion < 1) {
          const whiteHeight = minProjectHeight * (1 - rProportion);
          pdf.setDrawColor(203, 213, 225);
          pdf.setLineWidth(0.4);
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(rBarX, currentY + (minProjectHeight * rProportion), barWidth, whiteHeight, 1.5, 1.5, 'FD');
        }
        
        // Nom du projet à gauche (complet avec retour à la ligne si nécessaire)
        pdf.setFontSize(8.5);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        
        // Diviser le nom en plusieurs lignes si trop long
        const maxCharsPerLine = 22;
        const projectNameLines = [];
        let remainingName = project.projectName;
        
        while (remainingName.length > 0) {
          if (remainingName.length <= maxCharsPerLine) {
            projectNameLines.push(remainingName);
            break;
          } else {
            // Trouver le dernier espace avant la limite
            let cutIndex = remainingName.lastIndexOf(' ', maxCharsPerLine);
            if (cutIndex === -1 || cutIndex === 0) {
              cutIndex = maxCharsPerLine;
            }
            projectNameLines.push(remainingName.substring(0, cutIndex));
            remainingName = remainingName.substring(cutIndex).trim();
          }
        }
        
        // Afficher les lignes du nom avec espacement entre lettres
        const lineHeight = 3;
        const startY = currentY + minProjectHeight / 2 - (projectNameLines.length * lineHeight) / 2 + 2.5;
        let maxTextWidth = 0;
        
        projectNameLines.forEach((line, lineIndex) => {
          // Ajouter espacement entre les lettres manuellement
          const letterSpacing = 0.2;
          let xOffset = 0;
          for (let i = 0; i < line.length; i++) {
            pdf.text(line[i], chartX + 2 + xOffset, startY + lineIndex * lineHeight);
            xOffset += pdf.getTextWidth(line[i]) + letterSpacing;
          }
          // Garder la largeur maximale du texte
          if (xOffset > maxTextWidth) {
            maxTextWidth = xOffset;
          }
        });
        
        // Icône de check si projet complété (à droite du nom du projet)
        if (project.status && project.status.toLowerCase() === 'completed') {
          const checkX = chartX + 2 + maxTextWidth + 2;
          const checkY = currentY + minProjectHeight / 2;
          const checkSize = 3;
          
          // Cercle blanc avec bordure verte
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(16, 185, 129);
          pdf.setLineWidth(0.4);
          pdf.circle(checkX, checkY, checkSize / 2, 'FD');
          
          // Check mark vert
          pdf.setDrawColor(16, 185, 129);
          pdf.setLineWidth(0.6);
          pdf.line(checkX - 1, checkY, checkX - 0.3, checkY + 1);
          pdf.line(checkX - 0.3, checkY + 1, checkX + 1.2, checkY - 1);
        }
        
        // Valeur P à gauche de la barre P
        pdf.setFontSize(9);
        const pValueX = pBarX - 2;
        pdf.setTextColor(59, 130, 246);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`P: ${project.totalP.toFixed(1)}j`, pValueX, currentY + minProjectHeight / 2 + 1, { align: 'right' });
        
        // Valeur R à droite de la barre R
        const rValueX = rBarX + barWidth + 2;
        pdf.setTextColor(16, 185, 129);
        pdf.text(`R: ${project.totalR.toFixed(1)}j`, rValueX, currentY + minProjectHeight / 2 + 1);
        
        currentY += minProjectHeight + 3;
      });
      
      // Légende finale
      const legendY = currentY + 5;
      const legendX = chartX + 20;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      
      pdf.setFillColor(59, 130, 246);
      pdf.rect(legendX, legendY - 1.5, 6, 3.5, 'F');
      pdf.setTextColor(30, 41, 59);
      pdf.text('Planifie (P)', legendX + 8, legendY + 1.5);
      
      pdf.setFillColor(16, 185, 129);
      pdf.rect(legendX + 40, legendY - 1.5, 6, 3.5, 'F');
      pdf.text('Realise (R)', legendX + 48, legendY + 1.5);
      
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(203, 213, 225);
      pdf.rect(legendX + 80, legendY - 1.5, 6, 3.5, 'FD');
      pdf.text('Ecart', legendX + 88, legendY + 1.5);
      
      yPosition = legendY + 10;
      
      checkPageBreak(40);
      
      // 5. LES PROJETS TRAITÉS PENDANT CETTE PÉRIODE
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Les projets traites pendant cette periode', margin, yPosition);
      yPosition += 8;
      
      if (projects.length > 0) {
        const projectBoxHeight = 15;
        const projectsPerRow = 2; // 2 projets par ligne
        const projectBoxWidth = (contentWidth - 5) / projectsPerRow;
        const totalRows = Math.ceil(projects.length / projectsPerRow);
        const totalProjectsHeight = (projectBoxHeight * totalRows) + (5 * (totalRows - 1));
        
        // Vérifier si tous les projets peuvent tenir sur la page actuelle
        // Si non, passer à une nouvelle page avant de commencer
        if (yPosition + totalProjectsHeight + 25 > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          
          // Répéter le titre sur la nouvelle page
          pdf.setTextColor(30, 41, 59);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Les projets traites pendant cette periode (suite)', margin, yPosition);
          yPosition += 8;
        }
        
        projects.forEach((project, index) => {
          const col = index % projectsPerRow;
          const row = Math.floor(index / projectsPerRow);
          
          const x = margin + col * (projectBoxWidth + 5);
          const y = yPosition + row * (projectBoxHeight + 5);
          
          // Bordure
          pdf.setDrawColor(226, 232, 240);
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(x, y, projectBoxWidth, projectBoxHeight, 2, 2, 'FD');
          
          // Bullet point
          pdf.setFillColor(59, 130, 246);
          pdf.circle(x + 4, y + 6, 1.5, 'F');
          
          // Nom du projet (tronqué si trop long)
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 41, 59);
          const projectName = project.projectName.length > 20 
            ? project.projectName.substring(0, 17) + '...' 
            : project.projectName;
          pdf.text(projectName, x + 7, y + 7);
          
          // Icône de check si projet complété (à droite du nom)
          if (project.status && project.status.toLowerCase() === 'completed') {
            const checkX = x + 7 + pdf.getTextWidth(projectName) + 3;
            const checkY = y + 5.5;
            const checkSize = 2.5;
            
            // Cercle blanc avec bordure verte
            pdf.setFillColor(255, 255, 255);
            pdf.setDrawColor(16, 185, 129);
            pdf.setLineWidth(0.3);
            pdf.circle(checkX, checkY, checkSize / 2, 'FD');
            
            // Check mark vert
            pdf.setDrawColor(16, 185, 129);
            pdf.setLineWidth(0.5);
            pdf.line(checkX - 0.8, checkY, checkX - 0.2, checkY + 0.8);
            pdf.line(checkX - 0.2, checkY + 0.8, checkX + 1, checkY - 0.8);
          }
          
          // P et R (rectangles plus petits)
          const isTachesDivers = project.projectName === 'Tâches Divers';
          const statsWidth = isTachesDivers ? 22 : 44;
          const statsX = x + projectBoxWidth - statsWidth - 2;
          
          if (!isTachesDivers) {
            // P (réduit de 28 à 20)
            pdf.setFillColor(219, 234, 254);
            pdf.roundedRect(statsX, y + 3, 20, 9, 1, 1, 'F');
            pdf.setFontSize(6);
            pdf.setTextColor(30, 64, 175);
            pdf.setFont('helvetica', 'bold');
            pdf.text('P', statsX + 2, y + 8.5);
            pdf.setFontSize(8);
            pdf.text(project.totalP.toFixed(1), statsX + 18, y + 8.5, { align: 'right' });
          }
          
          // R (réduit de 28 à 20)
          const rX = isTachesDivers ? statsX : statsX + 24;
          pdf.setFillColor(209, 250, 229);
          pdf.roundedRect(rX, y + 3, 20, 9, 1, 1, 'F');
          pdf.setFontSize(6);
          pdf.setTextColor(6, 95, 70);
          pdf.setFont('helvetica', 'bold');
          pdf.text('R', rX + 2, y + 8.5);
          pdf.setFontSize(8);
          pdf.text(project.totalR.toFixed(1), rX + 18, y + 8.5, { align: 'right' });
        });
        
        yPosition += totalProjectsHeight + 10;
        
        // Total de projets (exclure "Tâches Divers")
        const projectCountExcludingTachesDivers = projects.filter(p => p.projectName !== 'Tâches Divers').length;
        pdf.setFillColor(219, 234, 254);
        pdf.roundedRect(margin, yPosition, contentWidth, 10, 2, 2, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text(`Total de projets: ${projectCountExcludingTachesDivers}`, pageWidth / 2, yPosition + 7, { align: 'center' });
        yPosition += 15;
      } else {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(120, 120, 120);
        pdf.text('Aucun projet dans la planification', margin + 5, yPosition);
        yPosition += 15;
      }
      
      // Nom du fichier
      const personName = focusedPerson.name.replace(/\s+/g, '_');
      const fileName = lastParams 
        ? `rapport_${personName}_${lastParams.dateDebut}_${lastParams.dateFin}.pdf`
        : `rapport_${personName}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    } finally {
      setIsExportingPersonModal(false);
    }
  };

  // Calculer le nombre de jours ouvrables (sans samedi/dimanche) pour la période
  const calculateWorkingDays = useMemo(() => {
    if (!lastParams) return 0;
    
    const startDate = new Date(lastParams.dateDebut);
    const endDate = new Date(lastParams.dateFin);
    let workingDays = 0;
    
    // Parcourir chaque jour de la période
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      // 0 = Dimanche, 6 = Samedi
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return workingDays;
  }, [lastParams]);

  // Fonction d'export PDF avec dom-to-image (meilleur pour les SVG)
  const exportToPDF = async () => {
    if (!bilanRef.current) return;
    
    setIsExporting(true);
    
    try {
      const element = bilanRef.current;
      
      // Masquer le bouton d'export temporairement
      const exportBtn = element.querySelector('.export-pdf-btn-bilan');
      if (exportBtn) exportBtn.style.display = 'none';
      
      // Attendre le rendu
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Capturer avec dom-to-image (meilleur pour SVG)
      const dataUrl = await domtoimage.toPng(element, {
        quality: 1.0,
        bgcolor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: element.scrollWidth + 'px',
          height: element.scrollHeight + 'px'
        }
      });
      
      // Restaurer le bouton
      if (exportBtn) exportBtn.style.display = 'flex';
      
      // Créer une image pour obtenir les dimensions
      const img = new Image();
      img.src = dataUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      // Calculer les dimensions pour le PDF
      const imgWidth = img.width;
      const imgHeight = img.height;
      
      // Ratio pour conversion px -> mm (à 96 DPI)
      const pxToMm = 0.264583;
      const imgWidthMm = imgWidth * pxToMm;
      const imgHeightMm = imgHeight * pxToMm;
      
      // Déterminer l'orientation
      const orientation = imgWidthMm > imgHeightMm ? 'landscape' : 'portrait';
      
      // Créer le PDF
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Dimensions A4
      const pdfWidth = orientation === 'portrait' ? 210 : 297;
      const pdfHeight = orientation === 'portrait' ? 297 : 210;
      
      // Marges
      const margin = 10;
      const maxWidth = pdfWidth - (margin * 2);
      const maxHeight = pdfHeight - (margin * 2);
      
      // Calculer le ratio pour adapter l'image
      const ratio = Math.min(maxWidth / imgWidthMm, maxHeight / imgHeightMm);
      const finalWidth = imgWidthMm * ratio;
      const finalHeight = imgHeightMm * ratio;
      
      // Centrer l'image
      const x = (pdfWidth - finalWidth) / 2;
      const y = margin;
      
      // Ajouter l'image au PDF
      pdf.addImage(dataUrl, 'PNG', x, y, finalWidth, finalHeight, undefined, 'FAST');
      
      // Nom du fichier avec période
      const fileName = lastParams 
        ? `bilan_equipe_${lastParams.dateDebut}_${lastParams.dateFin}.pdf`
        : `bilan_equipe_${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
      
      // Restaurer le bouton en cas d'erreur
      const exportBtn = bilanRef.current?.querySelector('.export-pdf-btn-bilan');
      if (exportBtn) exportBtn.style.display = 'flex';
    } finally {
      setIsExporting(false);
    }
  };

  // Fonction d'export PDF pour la section Performance uniquement
  const exportPerformanceToPDF = async () => {
    if (!performanceRef.current) return;
    
    setIsExportingPerformance(true);
    
    try {
      const element = performanceRef.current;
      
      // Masquer le bouton d'export temporairement
      const exportBtn = element.querySelector('.export-performance-btn');
      if (exportBtn) exportBtn.style.display = 'none';
      
      // Attendre le rendu
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Capturer avec dom-to-image
      const dataUrl = await domtoimage.toPng(element, {
        quality: 1.0,
        bgcolor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: element.scrollWidth + 'px',
          height: element.scrollHeight + 'px'
        }
      });
      
      // Restaurer le bouton
      if (exportBtn) exportBtn.style.display = 'flex';
      
      // Créer une image pour obtenir les dimensions
      const img = new Image();
      img.src = dataUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      // Calculer les dimensions pour le PDF
      const imgWidth = img.width;
      const imgHeight = img.height;
      
      // Ratio pour conversion px -> mm (à 96 DPI)
      const pxToMm = 0.264583;
      const imgWidthMm = imgWidth * pxToMm;
      const imgHeightMm = imgHeight * pxToMm;
      
      // Déterminer l'orientation (landscape pour le graphique)
      const orientation = 'landscape';
      
      // Créer le PDF
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Dimensions A4 landscape
      const pdfWidth = 297;
      const pdfHeight = 210;
      
      // Marges
      const margin = 10;
      const maxWidth = pdfWidth - (margin * 2);
      const maxHeight = pdfHeight - (margin * 2);
      
      // Calculer le ratio pour adapter l'image
      const ratio = Math.min(maxWidth / imgWidthMm, maxHeight / imgHeightMm);
      const finalWidth = imgWidthMm * ratio;
      const finalHeight = imgHeightMm * ratio;
      
      // Centrer l'image
      const x = (pdfWidth - finalWidth) / 2;
      const y = margin;
      
      // Ajouter l'image au PDF
      pdf.addImage(dataUrl, 'PNG', x, y, finalWidth, finalHeight, undefined, 'FAST');
      
      // Nom du fichier avec période
      const fileName = lastParams 
        ? `performance_equipe_${lastParams.dateDebut}_${lastParams.dateFin}.pdf`
        : `performance_equipe_${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
      
      // Restaurer le bouton en cas d'erreur
      const exportBtn = performanceRef.current?.querySelector('.export-performance-btn');
      if (exportBtn) exportBtn.style.display = 'flex';
    } finally {
      setIsExportingPerformance(false);
    }
  };

  // Fonction d'export PDF pour la section Répartition uniquement
  const exportRepartitionToPDF = async () => {
    if (!repartitionRef.current) return;
    
    setIsExportingRepartition(true);
    
    try {
      const element = repartitionRef.current;
      
      // Masquer le bouton d'export temporairement
      const exportBtn = element.querySelector('.export-repartition-btn');
      if (exportBtn) exportBtn.style.display = 'none';
      
      // Attendre le rendu
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Capturer avec dom-to-image
      const dataUrl = await domtoimage.toPng(element, {
        quality: 1.0,
        bgcolor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: element.scrollWidth + 'px',
          height: element.scrollHeight + 'px'
        }
      });
      
      // Restaurer le bouton
      if (exportBtn) exportBtn.style.display = 'flex';
      
      // Créer une image pour obtenir les dimensions
      const img = new Image();
      img.src = dataUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      // Calculer les dimensions pour le PDF
      const imgWidth = img.width;
      const imgHeight = img.height;
      
      // Ratio pour conversion px -> mm (à 96 DPI)
      const pxToMm = 0.264583;
      const imgWidthMm = imgWidth * pxToMm;
      const imgHeightMm = imgHeight * pxToMm;
      
      // Déterminer l'orientation
      const orientation = imgWidthMm > imgHeightMm ? 'landscape' : 'portrait';
      
      // Créer le PDF
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Dimensions A4
      const pdfWidth = orientation === 'portrait' ? 210 : 297;
      const pdfHeight = orientation === 'portrait' ? 297 : 210;
      
      // Marges
      const margin = 10;
      const maxWidth = pdfWidth - (margin * 2);
      const maxHeight = pdfHeight - (margin * 2);
      
      // Calculer le ratio pour adapter l'image
      const ratio = Math.min(maxWidth / imgWidthMm, maxHeight / imgHeightMm);
      const finalWidth = imgWidthMm * ratio;
      const finalHeight = imgHeightMm * ratio;
      
      // Centrer l'image
      const x = (pdfWidth - finalWidth) / 2;
      const y = margin;
      
      // Ajouter l'image au PDF
      pdf.addImage(dataUrl, 'PNG', x, y, finalWidth, finalHeight, undefined, 'FAST');
      
      // Nom du fichier avec période
      const fileName = lastParams 
        ? `repartition_taches_${lastParams.dateDebut}_${lastParams.dateFin}.pdf`
        : `repartition_taches_${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
      
      // Restaurer le bouton en cas d'erreur
      const exportBtn = repartitionRef.current?.querySelector('.export-repartition-btn');
      if (exportBtn) exportBtn.style.display = 'flex';
    } finally {
      setIsExportingRepartition(false);
    }
  };

  // Préparer les données pour le bar chart (performance par personne)
  const performanceData = useMemo(() => {
    if (!planificationData?.totauxParPersonne) return [];

    return Object.entries(planificationData.totauxParPersonne)
      .filter(([key]) => 
        key !== 'UNKNOWN_Non assigné' && 
        key !== '798787607_Direction General'
      ) // Exclure "Non assigné" et "Direction General"
      .map(([key, person]) => ({
        key: key, // Ajouter la clé pour pouvoir récupérer les projets
        name: person.fullName,
        pourcentage: person.pourcentageRealisation || 0,
        totalJours: person.totalJours || 0,
        sum_jourTravail: person.sum_jourTravail || 0,
        nombreTaches: person.nombreTaches || 0,
        completed: person.nombreTachesCompleted || 0,
        enCours: person.nombreTachesEnCours || 0,
        ouvert: person.nombreTachesOuvert || 0,
        enAttente: person.nombreTachesEnAttente || 0,
        aValider: person.nombreTachesAvalider || 0,
        bloque: person.nombreTachesBloque || 0,
        annule: person.nombreTachesAnnule || 0,
        joursRealises: person.joursRealises || 0,
        joursEnCours: person.joursEnCours || 0
      }))
      .sort((a, b) => b.pourcentage - a.pourcentage); // Trier par pourcentage décroissant
  }, [planificationData]);

  // Préparer les données pour le pie chart (répartition globale)
  const repartitionData = useMemo(() => {
    if (!planificationData?.statistiquesGlobales) return [];

    const stats = planificationData.statistiquesGlobales;
    return [
      { name: 'Terminées', value: stats.tachesCompleted || 0, color: STATUS_COLORS.completed },
      { name: 'En cours', value: stats.tachesEnCours || 0, color: STATUS_COLORS.enCours },
      { name: 'Ouvertes', value: stats.tachesOuvert || 0, color: STATUS_COLORS.ouvert },
      { name: 'En attente', value: stats.tachesEnAttente || 0, color: STATUS_COLORS.enAttente },
      { name: 'À valider', value: stats.tachesAvalider || 0, color: STATUS_COLORS.aValider },
      { name: 'Bloquées', value: stats.tachesBloque || 0, color: STATUS_COLORS.bloque },
      { name: 'Annulées', value: stats.tachesAnnule || 0, color: STATUS_COLORS.annule }
    ].filter(item => item.value > 0); // Ne garder que les statuts avec des valeurs
  }, [planificationData]);

  const statistiquesGlobales = planificationData?.statistiquesGlobales;

  // Tooltip personnalisé pour le bar chart
  const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{data.name}</p>
          <div className="tooltip-content">
            <p className="tooltip-highlight">Pourcentage: {data.pourcentage.toFixed(1)}%</p>
            <p>Total jours planifiés: {data.totalJours}</p>
            <p>Total jours réalisés: {data.sum_jourTravail}</p>
            <p>Total tâches: {data.nombreTaches}</p>
            <div className="tooltip-divider"></div>
            <p className="tooltip-section-title">Détail des tâches:</p>
            <p style={{ color: STATUS_COLORS.completed }}>✓ Terminées: {data.completed}</p>
            <p style={{ color: STATUS_COLORS.enCours }}>⟳ En cours: {data.enCours}</p>
            <p style={{ color: STATUS_COLORS.ouvert }}>○ Ouvertes: {data.ouvert}</p>
            <p style={{ color: STATUS_COLORS.enAttente }}>⏸ En attente: {data.enAttente}</p>
            <p style={{ color: STATUS_COLORS.aValider }}>✎ À valider: {data.aValider}</p>
            {data.bloque > 0 && <p style={{ color: STATUS_COLORS.bloque }}>⊗ Bloquées: {data.bloque}</p>}
            {data.annule > 0 && <p style={{ color: STATUS_COLORS.annule }}>✕ Annulées: {data.annule}</p>}
          </div>
        </div>
      );
    }
    return null;
  };

  // Tooltip personnalisé pour le pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / statistiquesGlobales.totalTaches) * 100).toFixed(1);
      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{data.name}</p>
          <p className="tooltip-highlight">{data.value} tâches ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  const renderClickableBar = (props) => {
    const { x, y, width, height, fill, payload } = props;

    return (
      <g
        style={{ cursor: 'pointer' }}
        onClick={() => openPersonReport(payload)}
      >
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={8}
          ry={8}
        />
      </g>
    );
  };

  if (!planificationData) {
    return (
      <div className="bilan-equipe-page">
        <div className="page-header">
          <h1 className="page-title">
            <svg className="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
            Bilan Équipe
          </h1>
        </div>
        <div className="empty-state">
          <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <p>Aucune donnée disponible</p>
          <p className="empty-hint">Veuillez d'abord charger les données depuis la page Planification</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bilan-equipe-page" ref={bilanRef}>
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <svg className="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
              Bilan Équipe
            </h1>
            {lastParams && (
              <p className="page-description">
                Données pour la période : {lastParams.dateDebut} – {lastParams.dateFin}
              </p>
            )}
          </div>
          <button 
            className="export-pdf-btn-bilan" 
            onClick={exportToPDF}
            disabled={isExporting}
            title="Exporter en PDF"
          >
            {isExporting ? (
              <>
                <svg className="btn-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                </svg>
                Export...
              </>
            ) : (
              <>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="12" y1="18" x2="12" y2="12"></line>
                  <line x1="9" y1="15" x2="15" y2="15"></line>
                </svg>
                Exporter PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Graphique 1: Performance par personne */}
      <div className="chart-container" ref={performanceRef}>
        <div className="chart-header">
          <div className="chart-header-content">
            <div>
              <h2 className="chart-title">Performance par personne</h2>
              <p className="chart-subtitle">Pourcentage de réalisation des tâches</p>
            </div>
            <button 
              className="export-performance-btn" 
              onClick={exportPerformanceToPDF}
              disabled={isExportingPerformance}
              title="Exporter Performance en PDF"
            >
              {isExportingPerformance ? (
                <>
                  <svg className="btn-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                  </svg>
                  Export...
                </>
              ) : (
                <>
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  Exporter PDF
                </>
              )}
            </button>
          </div>
        </div>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={performanceData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                tick={{ fill: '#1e293b', fontSize: 12, fontWeight: 600 }}
              />
              <YAxis 
                label={{ value: 'Pourcentage (%)', angle: -90, position: 'insideLeft', style: { fill: '#1e293b', fontWeight: 600 } }}
                tick={{ fill: '#1e293b', fontWeight: 600 }}
              />
              <Tooltip content={<CustomBarTooltip />} />
              <Bar
                dataKey="pourcentage"
                fill="#3b82f6"
                radius={[8, 8, 0, 0]}
                shape={renderClickableBar}
                onClick={(data) => openPersonReport(data)}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {statistiquesGlobales && (
          <div className="chart-footer">
            <div className="global-stat">
              <span className="global-stat-label">Pourcentage global de réalisation:</span>
              <span className="global-stat-value">{statistiquesGlobales.pourcentageGlobal?.toFixed(1)}%</span>
            </div>
          </div>
        )}

        {/* Boxes de détails pour chaque personne */}
        <div className="person-details-grid">
          {performanceData.map((person, index) => (
            <div 
              key={index} 
              className="person-detail-box"
              onClick={() => openPersonReport(person)}
              style={{ cursor: 'pointer' }}
            >
              <div className="person-detail-header">
                <h3 className="person-name">{person.name}</h3>
                <div className="person-percentage" style={{
                  background: person.pourcentage >= 75 ? '#10b981' : 
                             person.pourcentage >= 50 ? '#3b82f6' : 
                             person.pourcentage >= 25 ? '#f59e0b' : '#ef4444'
                }}>
                  {person.pourcentage.toFixed(1)}%
                </div>
              </div>
              
              <div className="person-detail-content">
                <div className="detail-row">
                  <span className="detail-label">Total jours planifiés:</span>
                  <span className="detail-value">{person.totalJours}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Total jours réalisés:</span>
                  <span className="detail-value">{person.sum_jourTravail}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Total tâches:</span>
                  <span className="detail-value">{person.nombreTaches}</span>
                </div>
                
                <div className="detail-divider"></div>
                
                <div className="detail-section-title">Détail des tâches:</div>
                
                <div className="detail-row" style={{ color: STATUS_COLORS.completed }}>
                  <span className="detail-label">✓ Terminées:</span>
                  <span className="detail-value">{person.completed}</span>
                </div>
                <div className="detail-row" style={{ color: STATUS_COLORS.enCours }}>
                  <span className="detail-label">⟳ En cours:</span>
                  <span className="detail-value">{person.enCours}</span>
                </div>
                <div className="detail-row" style={{ color: STATUS_COLORS.ouvert }}>
                  <span className="detail-label">○ Ouvertes:</span>
                  <span className="detail-value">{person.ouvert}</span>
                </div>
                <div className="detail-row" style={{ color: STATUS_COLORS.enAttente }}>
                  <span className="detail-label">⏸ En attente:</span>
                  <span className="detail-value">{person.enAttente}</span>
                </div>
                <div className="detail-row" style={{ color: STATUS_COLORS.aValider }}>
                  <span className="detail-label">✎ À valider:</span>
                  <span className="detail-value">{person.aValider}</span>
                </div>
                {person.bloque > 0 && (
                  <div className="detail-row" style={{ color: STATUS_COLORS.bloque }}>
                    <span className="detail-label">⊗ Bloquées:</span>
                    <span className="detail-value">{person.bloque}</span>
                  </div>
                )}
                {person.annule > 0 && (
                  <div className="detail-row" style={{ color: STATUS_COLORS.annule }}>
                    <span className="detail-label">✕ Annulées:</span>
                    <span className="detail-value">{person.annule}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de focus sur une personne */}
      {focusedPerson && (
        <div className="person-focus-modal" onClick={() => setFocusedPerson(null)}>
          <div className="person-focus-content" ref={personModalRef} onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => setFocusedPerson(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <button 
              className="export-person-modal-btn" 
              onClick={exportPersonModalToPDF}
              disabled={isExportingPersonModal}
              title="Exporter en PDF"
            >
              {isExportingPersonModal ? (
                <>
                  <svg className="btn-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                  </svg>
                  Export...
                </>
              ) : (
                <>
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  PDF
                </>
              )}
            </button>
            
            <div className="person-focus-header">
              <h2 className="person-focus-name">{focusedPerson.name}</h2>
            </div>
            
            {/* Bandeau de période - En haut avant tout */}
            <div className="focus-info-period-top">
              <span className="period-dates">📅 Période : {lastParams?.dateDebut} → {lastParams?.dateFin}</span>
            </div>
            
            <div className="person-focus-body">
              <div className="focus-section">
                <h3 className="focus-section-title">📊 Statistiques générales</h3>
                <div className="focus-stats-grid">
                  <div className="focus-stat-card">
                    <span className="focus-stat-label">Total jours planifiés</span>
                    <span className="focus-stat-value">{focusedPerson.totalJours}</span>
                  </div>
                  <div className="focus-stat-card">
                    <span className="focus-stat-label">Total jours réalisés</span>
                    <span className="focus-stat-value">{focusedPerson.sum_jourTravail}</span>
                  </div>
                  <div className="focus-stat-card">
                    <span className="focus-stat-label">Total tâches</span>
                    <span className="focus-stat-value">{focusedPerson.nombreTaches}</span>
                  </div>
                  <div className="focus-stat-card">
                    <span className="focus-stat-label">Taux de réalisation</span>
                    <span className="focus-stat-value">{focusedPerson.pourcentage.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
              
              {/* Graphique comparatif Planifié vs Réalisé */}
              <div className="focus-section">
                <h3 className="focus-section-title">📈 Comparaison Planifié vs Réalisé</h3>
                <div className="focus-chart-wrapper" style={{ width: '100%', padding: '20px 0' }}>
                  {(() => {
                    const projects = getPersonProjectsWithTotals(focusedPerson.key)
                      .filter(p => p.totalP > 0 || p.totalR > 0);
                    
                    const totalP = focusedPerson.totalJours;
                    const totalR = focusedPerson.sum_jourTravail;
                    const maxValue = Math.max(totalP, totalR, calculateWorkingDays);
                    
                    // Calculer la hauteur dynamique en fonction du nombre de projets
                    // Hauteur minimale par projet : 70px + 5px d'espacement
                    const minHeightPerProject = 75;
                    const baseHeight = 650;
                    const calculatedHeight = Math.max(baseHeight, projects.length * minHeightPerProject);
                    
                    return (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '60px', alignItems: 'flex-end' }}>
                        {/* Barre Planifié (P) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '350px' }}>
                          <div style={{ 
                            fontWeight: 700, 
                            fontSize: '20px', 
                            color: '#1e293b', 
                            marginBottom: '15px',
                            textAlign: 'center'
                          }}>
                            Total P: {totalP.toFixed(2)}j
                          </div>
                          <div style={{ 
                            width: '100%', 
                            height: `${calculatedHeight}px`, 
                            display: 'flex', 
                            flexDirection: 'column-reverse',
                            border: '3px solid #cbd5e1',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: '#f8fafc',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}>
                            {projects.map((project, index) => {
                              const maxProjectValue = Math.max(project.totalP, project.totalR);
                              const heightPercent = (maxProjectValue / maxValue) * 100;
                              const marginBottom = index > 0 ? '5px' : '0';
                              
                              // Calculer la proportion de P dans la barre
                              const pProportion = project.totalP > 0 ? (project.totalP / maxProjectValue) * 100 : 0;
                              const whiteProportion = 100 - pProportion;
                              
                              return (
                                <div 
                                  key={`P_${project.projectName}`}
                                  style={{ 
                                    width: '100%',
                                    height: `${heightPercent}%`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: marginBottom,
                                    position: 'relative',
                                    minHeight: '60px',
                                    transition: 'all 0.3s ease',
                                    border: '2px solid #cbd5e1',
                                    borderRadius: '6px',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {/* Partie colorée (P) */}
                                  {project.totalP > 0 && (
                                    <div style={{
                                      height: `${pProportion}%`,
                                      backgroundColor: '#3b82f6',
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      padding: '10px 12px',
                                      gap: '8px',
                                      minHeight: '50px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title={`Cliquez pour voir les détails - ${project.projectName}: ${project.totalP.toFixed(1)}j`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const tasks = getProjectTasks(focusedPerson.key, project.projectName);
                                      setTaskDetailsModal({
                                        projectName: project.projectName,
                                        type: 'P',
                                        tasks: tasks
                                      });
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#2563eb';
                                      e.currentTarget.style.transform = 'scale(1.02)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#3b82f6';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    >
                                      {project.status && project.status.toLowerCase() === 'completed' && (
                                        <svg 
                                          style={{ 
                                            width: '20px', 
                                            height: '20px', 
                                            minWidth: '20px',
                                            color: '#10b981',
                                            background: 'white',
                                            borderRadius: '50%',
                                            padding: '3px',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                            flexShrink: 0
                                          }}
                                          viewBox="0 0 24 24" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          strokeWidth="3"
                                        >
                                          <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                      )}
                                      <div style={{ 
                                        color: '#ffffffff',
                                        fontWeight: 800, 
                                        fontSize: '14px',
                                        textAlign: 'left',
                                        lineHeight: '1.4',
                                        wordBreak: 'break-word',
                                        flex: '1',
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                        letterSpacing: '0.5px'
                                      }}>
                                        {project.projectName}
                                      </div>
                                      <div style={{ 
                                        color: '#ffffff',
                                        fontWeight: 900, 
                                        fontSize: '17px',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                      }}>
                                        {project.totalP.toFixed(1)}j
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Partie blanche (différence) */}
                                  {whiteProportion > 0 && (
                                    <div style={{
                                      height: `${whiteProportion}%`,
                                      backgroundColor: '#ffffff',
                                      borderTop: project.totalP > 0 ? '2px dashed #cbd5e1' : 'none'
                                    }}></div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ 
                            fontWeight: 700, 
                            fontSize: '18px', 
                            color: '#3b82f6', 
                            marginTop: '15px',
                            textAlign: 'center'
                          }}>
                            Planifié (P)
                          </div>
                        </div>
                        
                        {/* Barre Réalisé (R) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '350px' }}>
                          <div style={{ 
                            fontWeight: 700, 
                            fontSize: '20px', 
                            color: '#1e293b', 
                            marginBottom: '15px',
                            textAlign: 'center'
                          }}>
                            Total R: {totalR.toFixed(1)}j
                          </div>
                          <div style={{ 
                            width: '100%', 
                            height: `${calculatedHeight}px`, 
                            display: 'flex', 
                            flexDirection: 'column-reverse',
                            border: '3px solid #cbd5e1',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: '#f8fafc',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}>
                            {projects.map((project, index) => {
                              const maxProjectValue = Math.max(project.totalP, project.totalR);
                              const heightPercent = (maxProjectValue / maxValue) * 100;
                              const marginBottom = index > 0 ? '5px' : '0';
                              
                              // Calculer la proportion de R dans la barre
                              const rProportion = project.totalR > 0 ? (project.totalR / maxProjectValue) * 100 : 0;
                              const whiteProportion = 100 - rProportion;
                              
                              return (
                                <div 
                                  key={`R_${project.projectName}`}
                                  style={{ 
                                    width: '100%',
                                    height: `${heightPercent}%`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: marginBottom,
                                    position: 'relative',
                                    minHeight: '60px',
                                    transition: 'all 0.3s ease',
                                    border: '2px solid #cbd5e1',
                                    borderRadius: '6px',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {/* Partie colorée (R) */}
                                  {project.totalR > 0 && (
                                    <div style={{
                                      height: `${rProportion}%`,
                                      backgroundColor: '#10b981',
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      padding: '10px 12px',
                                      gap: '8px',
                                      minHeight: '50px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title={`Cliquez pour voir les détails - ${project.projectName}: ${project.totalR.toFixed(1)}j`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const tasks = getProjectTasks(focusedPerson.key, project.projectName);
                                      setTaskDetailsModal({
                                        projectName: project.projectName,
                                        type: 'R',
                                        tasks: tasks
                                      });
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#059669';
                                      e.currentTarget.style.transform = 'scale(1.02)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#10b981';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    >
                                      {project.status && project.status.toLowerCase() === 'completed' && (
                                        <svg 
                                          style={{ 
                                            width: '20px', 
                                            height: '20px', 
                                            minWidth: '20px',
                                            color: '#10b981',
                                            background: 'white',
                                            borderRadius: '50%',
                                            padding: '3px',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                            flexShrink: 0
                                          }}
                                          viewBox="0 0 24 24" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          strokeWidth="3"
                                        >
                                          <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                      )}
                                      <div style={{ 
                                        color: '#1e293b',
                                        fontWeight: 800, 
                                        fontSize: '14px',
                                        textAlign: 'left',
                                        lineHeight: '1.4',
                                        wordBreak: 'break-word',
                                        flex: '1',
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        //textShadow: '1px 1px 2px rgba(250, 248, 248, 0.2)',
                                        letterSpacing: '0.5px'
                                      }}>
                                        {project.projectName}
                                      </div>
                                      <div style={{ 
                                        color: '#1e293b',
                                        fontWeight: 900, 
                                        fontSize: '17px',
                                        //textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                      }}>
                                        {project.totalR.toFixed(1)}j
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Partie blanche (différence) */}
                                  {whiteProportion > 0 && (
                                    <div style={{
                                      height: `${whiteProportion}%`,
                                      backgroundColor: '#ffffff',
                                      borderTop: project.totalR > 0 ? '2px dashed #cbd5e1' : 'none'
                                    }}></div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ 
                            fontWeight: 700, 
                            fontSize: '18px', 
                            color: '#10b981', 
                            marginTop: '15px',
                            textAlign: 'center'
                          }}>
                            Réalisé (R)
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Légende */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '40px',
                    marginTop: '35px',
                    fontSize: '16px',
                    fontWeight: 700
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}></div>
                      <span>Planifié (P)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        backgroundColor: '#10b981',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}></div>
                      <span>Réalisé (R)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        backgroundColor: '#ffffff',
                        border: '2px dashed #cbd5e1',
                        borderRadius: '4px'
                      }}></div>
                      <span>Écart</span>
                    </div>
                  </div>
                  
                  {/* Info max jours ouvrables */}
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '20px',
                    fontSize: '13px',
                    color: '#64748b',
                    fontStyle: 'italic',
                    fontWeight: 500
                  }}>
                  </div>
                </div>
              </div>
              
              {/* Section des projets travaillés */}
              <div className="focus-section">
                <h3 className="focus-section-title">🏢 Les projets traités pendant cette période</h3>
                <div className="focus-projects-list">
                  {getPersonProjectsWithTotals(focusedPerson.key).length > 0 ? (
                    getPersonProjectsWithTotals(focusedPerson.key).map((project, index) => {
                      const isTachesDivers = project.projectName === 'Tâches Divers';
                      
                      return (
                        <div key={index} className="project-item-with-stats">
                          <div className="project-name-section">
                            <span className="project-bullet">•</span>
                            <span className="project-name">{project.projectName}</span>
                            {project.status && project.status.toLowerCase() === 'completed' && (
                              <svg 
                                className="project-check-icon"
                                style={{ 
                                  width: '22px', 
                                  height: '22px', 
                                  minWidth: '22px',
                                  color: '#ffffff',
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  borderRadius: '50%',
                                  padding: '4px',
                                  boxShadow: '0 2px 6px rgba(16, 185, 129, 0.4)',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="3"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                          </div>
                          <div className="project-stats-section">
                            {!isTachesDivers && (
                              <div className="project-stat-box project-stat-p">
                                <span className="project-stat-label">P</span>
                                <span className="project-stat-value">{project.totalP.toFixed(1)}</span>
                              </div>
                            )}
                            <div className="project-stat-box project-stat-r">
                              <span className="project-stat-label">R</span>
                              <span className="project-stat-value">{project.totalR.toFixed(1)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-projects">
                      <span className="no-projects-icon">ℹ️</span>
                      <span className="no-projects-text">Aucun projet dans la planification pour cette période</span>
                    </div>
                  )}
                </div>
                <div className="projects-count">
                  <span className="projects-count-label">Total de projets:</span>
                  <span className="projects-count-value">
                    {getPersonProjectsWithTotals(focusedPerson.key).filter(p => p.projectName !== 'Tâches Divers').length}
                  </span>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      )}

      {/* Graphique 2: Répartition globale des tâches */}
      <div className="chart-container" ref={repartitionRef}>
        <div className="chart-header">
          <div className="chart-header-content">
            <div>
              <h2 className="chart-title">Répartition globale des tâches</h2>
              <p className="chart-subtitle">
                Total: {statistiquesGlobales?.totalTaches || 0} tâches
              </p>
            </div>
            <button 
              className="export-repartition-btn" 
              onClick={exportRepartitionToPDF}
              disabled={isExportingRepartition}
              title="Exporter Répartition en PDF"
            >
              {isExportingRepartition ? (
                <>
                  <svg className="btn-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                  </svg>
                  Export...
                </>
              ) : (
                <>
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  Exporter PDF
                </>
              )}
            </button>
          </div>
        </div>
        <div className="chart-wrapper pie-chart-wrapper">
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={repartitionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {repartitionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry) => `${value}: ${entry.payload.value}`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {statistiquesGlobales && (
          <div className="chart-footer">
            <div className="stats-grid">
              <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.completed }}>
                <span className="stat-label">Terminées</span>
                <span className="stat-value">{statistiquesGlobales.tachesCompleted}</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.enCours }}>
                <span className="stat-label">En cours</span>
                <span className="stat-value">{statistiquesGlobales.tachesEnCours}</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.ouvert }}>
                <span className="stat-label">Ouvertes</span>
                <span className="stat-value">{statistiquesGlobales.tachesOuvert}</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.enAttente }}>
                <span className="stat-label">En attente</span>
                <span className="stat-value">{statistiquesGlobales.tachesEnAttente}</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.aValider }}>
                <span className="stat-label">À valider</span>
                <span className="stat-value">{statistiquesGlobales.tachesAvalider}</span>
              </div>
              {statistiquesGlobales.tachesBloque > 0 && (
                <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.bloque }}>
                  <span className="stat-label">Bloquées</span>
                  <span className="stat-value">{statistiquesGlobales.tachesBloque}</span>
                </div>
              )}
              {statistiquesGlobales.tachesAnnule > 0 && (
                <div className="stat-card" style={{ borderLeftColor: STATUS_COLORS.annule }}>
                  <span className="stat-label">Annulées</span>
                  <span className="stat-value">{statistiquesGlobales.tachesAnnule}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de détails des tâches */}
        {taskDetailsModal && (
          <div className="task-details-modal" onClick={() => setTaskDetailsModal(null)}>
            <div className="task-details-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-modal-btn" onClick={() => setTaskDetailsModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              
              <div className="task-details-header">
                <h2 className="task-details-title">
                  {taskDetailsModal.projectName}
                </h2>
                <span className={`task-details-type-badge ${taskDetailsModal.type === 'P' ? 'type-p' : 'type-r'}`}>
                  {taskDetailsModal.type === 'P' ? 'Planifié (P)' : 'Réalisé (R)'}
                </span>
              </div>
              
              <div className="task-details-body">
                <div className="task-details-summary">
                  <div className="summary-item">
                    <span className="summary-label">Nombre de tâches:</span>
                    <span className="summary-value">{taskDetailsModal.tasks.length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">
                      {taskDetailsModal.type === 'P' ? 'Total jours planifiés:' : 'Total jours réalisés:'}
                    </span>
                    <span className="summary-value">
                      {taskDetailsModal.type === 'P' 
                        ? taskDetailsModal.tasks.reduce((sum, t) => sum + t.nombreJour, 0).toFixed(1)
                        : taskDetailsModal.tasks.reduce((sum, t) => sum + t.joursRealises, 0).toFixed(1)
                      }j
                    </span>
                  </div>
                </div>
                
                <div className="task-details-list">
                  {taskDetailsModal.tasks.map((task, index) => (
                    <div key={index} className="task-detail-card">
                      <div className="task-detail-header">
                        <h4 className="task-detail-name">{task.nomTache}</h4>
                        <span className={`task-detail-status status-${task.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                          {task.status}
                        </span>
                      </div>
                      <div className="task-detail-info">
                        <div className="task-detail-row">
                          <span className="task-detail-label">
                            {taskDetailsModal.type === 'P' ? 'Jours planifiés:' : 'Jours réalisés:'}
                          </span>
                          <span className="task-detail-value task-detail-days">
                            {taskDetailsModal.type === 'P' 
                              ? `${task.nombreJour.toFixed(1)}j`
                              : `${task.joursRealises.toFixed(1)}j`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BilanEquipe;
